import React, {useEffect, useState} from 'react';
import {Box, Button, CircularProgress, Tab, Tabs} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {retrieveCurrentSchool, retrieveProfile} from './ProfilesSlice';
import Typography from '@mui/material/Typography';
import {Link, Outlet, useLocation, useNavigate, useParams} from 'react-router-dom';
import {tabs} from './const/ProfileDetailTabs';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import useUser from '../../../utility/hooks/useUser';
import {createSelector} from '@reduxjs/toolkit';
import {QueryStatus} from '@reduxjs/toolkit/query';
import {getRouteMatchPath} from '../../../utility/routeUtil';
import routes from '../../../routes/routes';
import {resetTableState} from '../../../store/tableSlice';
import ReadonlySectionMessage from './Components/ReadonlySectionMessage';

const selectQueriesAndMutations = state => ({
    queries: state.api.queries,
    mutations: state.api.mutations,
});

const selectIsSomeQueryPending = createSelector(
    [selectQueriesAndMutations],
    ({
         queries,
         mutations
     }) => {
        for (const query of Object.values(queries)) {
            if (query.status === QueryStatus.pending) {
                return true;
            }
        }

        for (const mutation of Object.values(mutations)) {
            if (mutation.status === QueryStatus.pending) {
                return true;
            }
        }

        return false;
    }
);

const StudentProfileDetails = () => {
    const isProfileInfoLoading = useSelector(state => state.profilesInfo.isLoading);
    const selectedProfile = useSelector(state => state.profilesInfo.selectedProfile);
    const updatingSuccessful = useSelector(state => state.profilesInfo.updatingSuccessful);
    const currentSchool = useSelector(state => state.profilesInfo.currentSchool);
    const {studentId} = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {user} = useUser();
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const [profileTabs, setProfileTabs] = useState([]);
    const isSomeQueryPending = useSelector(selectIsSomeQueryPending);
    const {pathname} = useLocation();
    const path = getRouteMatchPath(routes, pathname);
    const isSchoolSwitching = useSelector(state => state.application.isSchoolSwitching);
    const account = useSelector(state => state.application.account);

    useEffect(() => {
        dispatch(retrieveCurrentSchool());

        return () => {
            dispatch(resetTableState(path));
        };
    }, [dispatch]);

    useEffect(() => {
        if (selectedProfile.id) {
            setProfileTabs(tabs
                .filter(tab => tab.shouldRender(
                    user.permissions,
                    user.parentPermissions,
                    selectedProfile.personType,
                    selectedProfile.isParentOfStudent,
                    user.roles
                ))
                .map(tab => typeof tab.label === 'function'
                    ? {
                        ...tab,
                        label: tab.label(selectedProfile.personType)
                    }
                    : tab)
            );
        }
    }, [selectedProfile.id, user.permissions.length, user.parentPermissions, selectedProfile.isParentOfStudent]);

    useEffect(() => {
        if (profileTabs.length === 0) return;

        const clean = pathname.replace(/\/+$/, '');
        const parts = clean.split('/');
        const pIndex = parts.indexOf('profiles');

        const segment = pIndex !== -1 && parts.length > pIndex + 3
            ? parts[pIndex + 3]
            : '';

        const id = profileTabs.findIndex(t => t.route === segment);
        if (id !== -1) {
            setCurrentTabIndex(id);
            return;
        }

        if (isSchoolSwitching) {
            return;
        }

        navigate(`${clean}/${profileTabs[0].route}`, { replace: true });
        setCurrentTabIndex(0);
    }, [pathname, profileTabs, navigate, isSchoolSwitching]);

    useEffect(() => {
        if (!studentId) {
            return undefined;
        }

        let cancelled = false;

        dispatch(retrieveProfile({studentId}))
            .unwrap()
            .catch(() => {
                if (!cancelled) {
                    navigate('/profiles', {replace: true});
                }
            });

        return () => {
            cancelled = true;
        };
    }, [dispatch, studentId, updatingSuccessful, navigate]);

    const handleChange = (event, newValue) => {
        setCurrentTabIndex(newValue);
        navigate(profileTabs[newValue].route);
    };

    const buildConfirmMessage = (profile) => {
        return profile.needsConfirmation?.length !== 0
            ? 'Details that still require confirmation: ' + profile.needsConfirmation
            : '';
    };

    return (
        <>
            <Box>
                <Button
                    sx={{mt: 2}}
                    variant="text"
                    startIcon={<ArrowBackRoundedIcon/>}
                    onClick={() => navigate('/profiles')}
                >
                    Back
                </Button>
            </Box>
            {
                selectedProfile.id
                    ? <>
                        <Typography variant="h4">Personal Details
                            for {selectedProfile.name} {selectedProfile.surname}</Typography>
                        <Typography variant="body1" color="red"
                                    paddingTop={1}>{buildConfirmMessage(selectedProfile)}</Typography>
                        <ReadonlySectionMessage
                                school={currentSchool}
                                studentProfile={selectedProfile}
                                userAccount={account}/>
                        {
                            profileTabs.length > 0
                                ? <Tabs
                                    value={currentTabIndex}
                                    onChange={handleChange}
                                    variant="scrollable"
                                    textColor="inherit"
                                    allowScrollButtonsMobile
                                >
                                    {
                                        profileTabs.map((tab, index) => (
                                            <Tab
                                                key={index}
                                                tabIndex={index}
                                                label={tab.label}
                                                component={Link}
                                                to={tab.route}
                                            />
                                        ))
                                    }
                                </Tabs>
                                : ''
                        }
                    </>
                    : null
            }
            <Box
                position="relative"
                minHeight={40}
                marginTop={2}
                display="flex"
                flexGrow={1}
                flexDirection="column"
            >
                <Outlet context={{key: path}}/>
                {(
                    ((isSomeQueryPending || isProfileInfoLoading) && profileTabs[currentTabIndex]?.showLoadingSpinner) ||
                    (isProfileInfoLoading && !selectedProfile.id)
                ) && (
                    <>
                        <Box
                            display="block"
                            position="absolute"
                            top={0}
                            left={0}
                            width="100%"
                            height="100%"
                            sx={{
                                backgroundColor: 'rgb(250, 250, 251)',
                                zIndex: 1
                            }}
                        />
                        <Box
                            position="fixed"
                            top="55%"
                            left="50%"
                            sx={{
                                transform: 'translate(-50%, -50%)',
                                zIndex: 1
                            }}
                        >
                            <CircularProgress size={40}/>
                        </Box>
                    </>
                )}
            </Box>
        </>);
};

export default StudentProfileDetails;
